/**
 * OCR Highlighting Utilities
 *
 * Helper functions for finding and highlighting text in OCR results
 */

export interface OCRBoundingBox {
  text: string;
  page_number: number;
  page_width: number;
  page_height: number;
  unit: string;
  bounding_box: [number, number, number, number, number, number, number, number];
  confidence: number;
}

export interface OCRResult {
  extracted_text: string;
  ocr_confidence: number;
  total_pages: number;
  pages: Array<{
    page_number: number;
    width: number;
    height: number;
    unit: string;
    lines: Array<{
      text: string;
      confidence: number;
      bounding_box: number[];
      words: Array<{
        text: string;
        confidence: number;
        bounding_box: number[];
      }>;
    }>;
    words: Array<{
      text: string;
      confidence: number;
      bounding_box: number[];
      line_index: number;
      page_number: number;
    }>;
  }>;
}

/**
 * Find all occurrences of a text value in OCR results
 */
export const findTextLocations = (
  ocrResult: OCRResult | null,
  searchText: string
): OCRBoundingBox[] => {
  if (!ocrResult || !ocrResult.pages) return [];

  const locations: OCRBoundingBox[] = [];
  const searchLower = searchText.toString().toLowerCase().trim();

  for (const page of ocrResult.pages) {
    for (const word of page.words || []) {
      if (word.text.toLowerCase() === searchLower) {
        locations.push({
          text: word.text,
          page_number: page.page_number,
          page_width: page.width,
          page_height: page.height,
          unit: page.unit,
          bounding_box: word.bounding_box as [
            number,
            number,
            number,
            number,
            number,
            number,
            number,
            number,
          ],
          confidence: word.confidence,
        });
      }
    }
  }

  return locations;
};

/**
 * Find locations for multiple field values
 */
export const findMultipleTextLocations = (
  ocrResult: OCRResult | null,
  fields: Array<{ fieldName: string; value: string }>
): Record<string, OCRBoundingBox[]> => {
  const result: Record<string, OCRBoundingBox[]> = {};

  for (const field of fields) {
    result[field.fieldName] = findTextLocations(ocrResult, field.value);
  }

  return result;
};

/**
 * Check if a value exists in OCR results
 */
export const valueExistsInOCR = (
  ocrResult: OCRResult | null,
  searchText: string
): boolean => {
  return findTextLocations(ocrResult, searchText).length > 0;
};

/**
 * Get confidence for a specific value
 */
export const getValueConfidence = (
  ocrResult: OCRResult | null,
  searchText: string
): number | null => {
  const locations = findTextLocations(ocrResult, searchText);
  if (locations.length === 0) return null;

  // Return average confidence if multiple occurrences
  const avgConfidence =
    locations.reduce((sum, loc) => sum + loc.confidence, 0) / locations.length;
  return avgConfidence;
};

/**
 * Get readable field names for display
 */
export const getFieldDisplayName = (fieldName: string): string => {
  const nameMap: Record<string, string> = {
    employeeName: 'Employee Name',
    employeeId: 'Employee ID',
    regularHours: 'Regular Hours',
    overtimeHours: 'Overtime Hours',
    regularRate: 'Hourly Rate',
    overtimeRate: 'Overtime Rate',
    totalPay: 'Total Pay',
  };

  return nameMap[fieldName] || fieldName;
};

/**
 * Get color for field type
 */
export const getFieldColor = (fieldName: string): string => {
  const colorMap: Record<string, string> = {
    employeeName: '#3B82F6', // blue
    employeeId: '#8B5CF6', // purple
    regularHours: '#10B981', // green
    overtimeHours: '#F59E0B', // amber
    regularRate: '#EF4444', // red
    overtimeRate: '#EC4899', // pink
    totalPay: '#06B6D4', // cyan
  };

  return colorMap[fieldName] || '#6B7280'; // default gray
};
